import { EmptyState, type EmptyStateProps, RefreshIcon } from "@hightouch/htui";
import type { ReactNode } from "react";
import analyticsPlaceholder from "src/assets/placeholders/goals.svg";
import funnelsPlaceholder from "src/assets/placeholders/funnels.svg";
import experimentsPlaceholder from "src/assets/placeholders/experiments.svg";
import { PermissionedButton } from "src/components/permission";
import { Link } from "src/router";
import { runChartPermission } from "./permissions";
import { useChartDataContext } from "./state";

const docsUrl = (page: string) =>
  `${import.meta.env.VITE_DOCS_URL}/campaign-intelligence/${page}`;

const AnalyticsEmptyState = (props: EmptyStateProps) => (
  <EmptyState
    variant="minimal"
    flex={1}
    width="100%"
    maxWidth="576px"
    mx="auto"
    messageMaxW="100%"
    {...props}
  />
);

export const AnalyticsBasePlaceholder = () => (
  <AnalyticsEmptyState
    imageUrl={analyticsPlaceholder}
    title="Analytics are not available yet"
    message={
      <>
        This page allows you to create visualizations of audiences across
        user-defined metrics such as “Total revenue”, “Count of purchase
        events”, or “Average lifetime value”. You can compare different
        audiences, analyze a single audience, or compare split groups within an
        audience.
        <br />
        Check out our{" "}
        <Link isExternal href={docsUrl("charts")}>
          Charts documentation
        </Link>{" "}
        to learn more.
      </>
    }
  />
);

export const UnconfiguredPlaceholder = ({
  parentModelId,
}: {
  parentModelId?: number | null;
}) => (
  <AnalyticsEmptyState
    imageUrl={analyticsPlaceholder}
    title="Select a metric then refresh to view results"
    message={
      <>
        An insights chart is comprised of a metric and one or more audiences.
        <br />
        Check out our{" "}
        <Link isExternal href={docsUrl("insights")}>
          Insights chart documentation
        </Link>{" "}
        to learn more.
      </>
    }
    actions={<RefreshButton parentModelId={parentModelId} />}
  />
);

export const ExperimentsPlaceholder = ({
  errorMessage,
  parentModelId,
}: {
  errorMessage?: ReactNode;
  parentModelId?: number | null;
}) =>
  errorMessage ? (
    <AnalyticsEmptyState
      imageUrl={experimentsPlaceholder}
      title="No data available"
      message={errorMessage}
    />
  ) : (
    <AnalyticsEmptyState
      imageUrl={experimentsPlaceholder}
      title="Select a measurement and metric then refresh to view results"
      message={
        <>
          Check out our{" "}
          <Link isExternal href={docsUrl("experiments")}>
            Splits chart documentation
          </Link>{" "}
          to learn more.
        </>
      }
      actions={<RefreshButton parentModelId={parentModelId} />}
    />
  );

export const FunnelsPlaceholder = ({
  parentModelId,
}: {
  parentModelId?: number | null;
}) => (
  <AnalyticsEmptyState
    imageUrl={funnelsPlaceholder}
    title="Select steps then refresh to view results"
    message={
      <>
        A funnel chart is comprised of two or more steps and one or more
        audiences.
        <br />
        Check out our{" "}
        <Link isExternal href={docsUrl("funnels")}>
          Funnels chart documentation
        </Link>{" "}
        to learn more.
      </>
    }
    actions={<RefreshButton parentModelId={parentModelId} />}
  />
);

export const FunnelsNoDataPlaceholder = ({
  parentModelId,
}: {
  parentModelId?: number | null;
}) => {
  const { lastRanAt } = useChartDataContext();
  if (lastRanAt === null)
    return <FunnelsPlaceholder parentModelId={parentModelId} />;

  return (
    <AnalyticsEmptyState
      imageUrl={funnelsPlaceholder}
      title="No data"
      message="No data was returned. Adjust the query and try again."
    />
  );
};

export const PermissionsPlaceholder = () => (
  <AnalyticsEmptyState
    imageUrl={analyticsPlaceholder}
    title="You do not have permissions to use Charts"
    message={
      <>
        Contact a workspace admin to gain access or check out our{" "}
        <Link isExternal href={docsUrl("overview")}>
          Charts documentation
        </Link>{" "}
        to learn more.
      </>
    }
  />
);

export const NoDataPlaceholder = ({
  errorMessage,
  parentModelId,
}: {
  errorMessage?: string | ReactNode;
  parentModelId?: number | null;
}) => {
  const { lastRanAt } = useChartDataContext();
  if (lastRanAt === null)
    return <UnconfiguredPlaceholder parentModelId={parentModelId} />;

  return (
    <AnalyticsEmptyState
      imageUrl={analyticsPlaceholder}
      title="No data"
      message={errorMessage}
      messageMaxW="400px"
    />
  );
};

const RefreshButton = ({
  parentModelId,
}: {
  parentModelId?: number | null;
}) => {
  const { validQuery, runQuery } = useChartDataContext();

  return (
    <PermissionedButton
      permission={runChartPermission(parentModelId?.toString())}
      icon={RefreshIcon}
      onClick={runQuery}
      variant="primary"
      isDisabled={!validQuery}
    >
      Refresh
    </PermissionedButton>
  );
};
